import React, { useCallback, useContext } from 'react'

import PlannedExperienceCtaContent from './Experience/PlannedExperienceCtaContent'
import { ItemCardVariant } from './ItemCard'
import ItineraryItemCard from './ItineraryItemCard'
import { PlannedItemPriceContent } from './PlannedItemPriceContent'
import SavedOfferCard from '../SavedOffers/SavedOfferCard'

import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import GeoContext from 'contexts/geoContext'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import BookmarkLocation from 'tripPlanner/components/Bookmark/BookmarkCardNew/BookmarkLocation'
import TripSummaryCardLoadingSkeleton from 'tripPlanner/components/TripSummary/TripSummaryCardLoadingSkeleton'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useExperience } from 'tripPlanner/hooks/api'
import { ExperienceItem } from 'tripPlanner/types/tripItem'
import { formatDateTimeOrDateRange, isLeBookmark } from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'
import { isExperienceAvailable } from 'tripPlanner/utils/experience'

interface ExperienceItemCardWithLoadingProps {
  item: ExperienceItem
  onClickCard: (offerLink: string) => void
  onClickTripName?: () => void
  variant: ItemCardVariant
}

export type ExperienceItemCardProps = {
  experience: App.ExperienceOffer
  item: ExperienceItem
  onClickCard: (offerLink: string) => void
  onClickTripName?: () => void
  variant: ItemCardVariant
}

export function ExperienceItemCard({
  experience,
  item,
  onClickCard,
  onClickTripName,
  variant,
}: ExperienceItemCardProps) {
  const geo = useContext(GeoContext)
  const trackEvent = useTrackExperienceOfferEvent()
  const tripContext = useContext(TripContext)
  const viewAsTemplate = tripContext?.viewAsTemplate
  const productTypeLabel = experience ? (
    <ProductTypeLabel productType={experience.productType} />
  ) : null
  const handleClick = useCallback(
    (offerLink?: string) => {
      trackEvent('OFFER_TILE_CLICKED', [
        {
          type: 'click',
          data: {
            offer: experience,
          },
        },
      ])
      if (offerLink) {
        onClickCard(offerLink)
      }
    },
    [experience, onClickCard, trackEvent],
  )

  const timeFormat = useRegionTimeFormat()

  const timeDescription = formatDateTimeOrDateRange(item, timeFormat)

  const placeId = 'id' in item.place ? item.place.id : undefined
  const imgId = experience.images[0]?.id
  const imgSrc = experience.images[0]?.url

  if (isLeBookmark(item)) {
    const tileLink =
      buildProductLink(item, !viewAsTemplate, { experience }) ?? ''

    const isOfferAvailable = isExperienceAvailable(experience, viewAsTemplate)

    let priceAreaContent: JSX.Element | null = null

    if (viewAsTemplate) {
      priceAreaContent = (
        <PlannedItemPriceContent
          discountPercent={experience.discount}
          price={experience.price}
          currencyCode={geo.currentCurrency}
          isOfferAvailable={isOfferAvailable}
          priceDisplayType="taxesAndFeesInclusive"
        />
      )
    } else {
      const { price, discountPercent, currencyCode, priceDisplayType } =
        item.savedItemData
      priceAreaContent = (
        <PlannedItemPriceContent
          discountPercent={discountPercent}
          price={price}
          currencyCode={currencyCode}
          isOfferAvailable={isOfferAvailable}
          priceDisplayType={priceDisplayType}
        />
      )
    }

    const ctaAreaContent = (
      <PlannedExperienceCtaContent
        viewOffer={() => handleClick(tileLink)}
        item={item}
        isOfferAvailable={isOfferAvailable}
        variant={variant}
      />
    )

    const locationDescription = (
      <BookmarkLocation
        variant="with_experience"
        startLocation={experience.location.name}
        typeOfExperience={experience.primaryCategory?.name}
        showIcon={false}
      />
    )

    return (
      <SavedOfferCard
        item={item}
        name={item.name}
        imgId={imgId}
        imgSrc={imgSrc}
        imageOverlayContent={productTypeLabel}
        locationDescription={locationDescription}
        descriptors={[timeDescription]}
        placeId={placeId}
        priceAreaContent={priceAreaContent}
        ctaAreaContent={ctaAreaContent}
        isSoldOut={!isOfferAvailable}
        testId="saved-experience-card"
        onClick={() => handleClick(tileLink)}
        offerLink={tileLink}
        onClickTripName={onClickTripName}
        variant={variant}
      />
    )
  } else {
    return (
      <ItineraryItemCard
        item={item}
        name={item.name}
        imgId={imgId}
        imgSrc={imgSrc}
        imageOverlayContent={productTypeLabel}
        locationDescription={experience.location.name}
        placeId={placeId}
        descriptors={[timeDescription]}
        address={item.place.address}
        onClickTripName={onClickTripName}
        variant={variant}
      />
    )
  }
}

function ExperienceItemCardWithLoader({
  item,
  onClickCard,
  onClickTripName,
  variant,
}: ExperienceItemCardWithLoadingProps) {
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)

  let experienceId: string | undefined
  if (item.code) {
    experienceId = item.code
  }
  const { data: experience, isLoading } = useExperience({
    currentCurrency,
    currentRegionCode,
    experienceId,
  })
  if (isLoading) {
    return <TripSummaryCardLoadingSkeleton />
  }
  if (!experience) {
    return null
  }
  return (
    <ExperienceItemCard
      experience={experience}
      item={item}
      onClickCard={onClickCard}
      onClickTripName={onClickTripName}
      variant={variant}
    />
  )
}

export default ExperienceItemCardWithLoader
